// No direct helper exists for parsing stream id in this project; copy broadcaster's logic for now
import { Settings, } from '@streamr/tv-engine';
import { executeSoon } from '@streamr/tv-utils';
import { binaryToHex, hexToBinary, toUserId, toUserIdRaw, } from '@streamr/utils';
import { KeyStore } from '../../engine/helpers/KeyStore';
import { StreamrAppApi } from '../../engine/StreamrAppApi';
import { OgrafManager } from '../../engine/streamrapp/ograf/OgrafManager';
import { UserManager } from '../../engine/streamrapp/UserManager';
import { UserStore } from '../../engine/streamrapp/UserStore';
import { canonicalUserId } from '../../utils/canonicalUserId';
import { formFullStreamId, formShortStreamId, generateStreamId, } from '../../utils/streamId';
import { formBroadcastUrl, parseBroadcastFromUrl } from '../../utils/url';
import { BroadcastStateService } from '../broadcaststate/BroadcastStateService';
import { DestinationsService } from '../destinations/DestinationsService';
import { LobbyService } from '../lobby/LobbyService';
import { MediaService } from '../media/MediaService';
import { OgrafService } from '../ograf/OgrafService';
import { PluginApiService } from '../pluginapi/PluginApiService';
import { PurchaseService } from '../purchase/PurchaseService';
import { SceneService } from '../scene/SceneService';
import { ScheduleService } from '../schedule/ScheduleService';
import { UserService } from '../user/UserService';
import { AcceptedParticipantType, UserRole, UserServiceOutput, } from '../user/UserServiceOutput';
import { VideoPropertiesService } from '../videoProperties/VideoPropertiesService';
import { ViewerCountService } from '../viewercount/ViewerCountService';
import { StreamrAppServiceOutput, } from './StreamrAppServiceOutput';
export class StreamrAppService {
    static instance;
    static getInstance() {
        if (!StreamrAppService.instance) {
            StreamrAppService.instance = new StreamrAppService();
        }
        return StreamrAppService.instance;
    }
    static getNotificationInterface() {
        return StreamrAppService.getInstance();
    }
    static getOutput() {
        return StreamrAppServiceOutput.getReactiveOutput();
    }
    // --- Singleton end
    roleComposites(role) {
        return (role === UserRole.SPEAKER ||
            role === UserRole.COHOST ||
            role === UserRole.CAMERA);
    }
    roleAddsLocalTracks(role) {
        return (role === UserRole.SPEAKER ||
            role === UserRole.COHOST ||
            role === UserRole.CAMERA ||
            role === UserRole.GUEST);
    }
    roleUsesSpeechDetection(role) {
        return (role === UserRole.SPEAKER ||
            role === UserRole.COHOST ||
            role === UserRole.CAMERA);
    }
    isIos = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    roleUsesDirectVideoOutput(role) {
        return (this.isIos && (role === UserRole.VIEWER || role === UserRole.GUEST));
    }
    async activateDirectVideoOutput() {
        if (this.output.getDirectVideoTrack()) {
            console.log('StreamrAppService: direct video output already active, skipping');
            return;
        }
        const mediaManager = this.streamrAppApi?.getMediaManager();
        if (!mediaManager)
            return;
        const track = await mediaManager.enableDirectVideoRendering();
        this.output.setDirectVideoTrack(track);
        console.log('StreamrAppService: direct video rendering activated (player worker kept alive for sync)');
    }
    async deactivateDirectVideoOutput() {
        const mediaManager = this.streamrAppApi?.getMediaManager();
        if (!mediaManager)
            return;
        const existingTrack = this.output.getDirectVideoTrack();
        if (!existingTrack)
            return;
        this.output.setDirectVideoTrack(null);
        existingTrack.stop();
        await mediaManager.stopVideoDecoderWorker();
        await mediaManager.stopVideoPlayerWorker();
        console.log('StreamrAppService: direct video rendering deactivated');
    }
    getVideoProfileForRole(role) {
        const isCall = UserServiceOutput.getAcceptedParticipantType() ===
            AcceptedParticipantType.SPEAKERS;
        let profile;
        switch (role) {
            case UserRole.COHOST:
                profile = isCall
                    ? {
                        width: Settings.cohostCallVideoWidth,
                        height: Settings.cohostCallVideoHeight,
                        bitrate: Settings.cohostCallVideoBitrate,
                    }
                    : {
                        width: Settings.cohostVideoWidth,
                        height: Settings.cohostVideoHeight,
                        bitrate: Settings.cohostVideoBitrate,
                    };
                break;
            case UserRole.SPEAKER:
                profile = {
                    width: Settings.speakerVideoWidth,
                    height: Settings.speakerVideoHeight,
                    bitrate: Settings.speakerVideoBitrate,
                };
                break;
            case UserRole.GUEST:
                profile = {
                    width: Settings.guestVideoWidth,
                    height: Settings.guestVideoHeight,
                    bitrate: Settings.guestVideoBitrate,
                };
                break;
            case UserRole.CAMERA:
                profile = {
                    width: Settings.cameraVideoWidth,
                    height: Settings.cameraVideoHeight,
                    bitrate: Settings.cameraVideoBitrate,
                };
                break;
            default:
                profile = {
                    width: Settings.speakerVideoWidth,
                    height: Settings.speakerVideoHeight,
                    bitrate: Settings.speakerVideoBitrate,
                };
                break;
        }
        return profile;
    }
    mediaService;
    streamrAppApi;
    output;
    broadcastActivated = false;
    checkStreamOnlineIntervalId;
    currentStreamId;
    lastFrameReceivedAt;
    currentBroadcast;
    displayConfigVersion = 0;
    liveConfigVersion = 0;
    thumbnailCaptureTimer;
    transferredThumbnailCanvasIds = new Set();
    thumbnailTransferRetries = {};
    thumbnailCanvasMap = new Map();
    thumbnailSyncInFlight = false;
    activeThumbnailParticipantIds = [];
    pendingCanvas;
    canvasReadyResolve;
    canvasReadyPromise;
    latestVideoTracks = [];
    latestAudioTracks = [];
    userManager;
    renderingReadyResolve;
    renderingReadyPromise;
    broadcastMetadataIntervalId;
    pendingCameraOneToOneStreamUrl;
    lowEndModeEnabled = false;
    engineStartedResolve;
    engineStartedPromise;
    constructor() {
        this.output = new StreamrAppServiceOutput();
        this.mediaService = MediaService.getInstance();
        this.mediaService.on('mediaStreamChanged', () => {
            void this.onMediaStreamChanged();
        });
        this.mediaService.on('additionalTrackAdded', this.onAdditionalTrackAdded);
        this.mediaService.on('additionalTrackRemoved', this.onAdditionalTrackRemoved);
        this.lastFrameReceivedAt = 0;
        const sceneService = SceneService.getInstance();
        sceneService.on('displaySceneChanged', this.onDisplaySceneChanged);
        sceneService.on('liveSceneChanged', this.onLiveSceneChanged);
    }
    async init() {
        // No initialization needed
    }
    notifyCanvasMounted(canvas) {
        this.pendingCanvas = canvas;
        if (this.streamrAppApi) {
            this.streamrAppApi
                .getMediaManager()
                .setDisplayCanvas(canvas)
                .then(() => {
                this.canvasReadyResolve?.();
                if (this.broadcastActivated) {
                    void this.onMediaStreamChanged();
                }
            })
                .catch((err) => {
                console.warn('[StreamrAppService] setDisplayCanvas failed in notifyCanvasMounted', err);
            });
        }
    }
    notifyLiveSceneThumbnailCanvasMounted(canvas) {
        this.streamrAppApi
            ?.getMediaManager()
            .setLiveSceneThumbnailCanvasMounted(canvas)
            .catch(() => undefined);
    }
    notifyCanvasClicked(x, y) {
        this.streamrAppApi?.handleCanvasClick(x, y).catch(console.error);
    }
    onDisplaySceneChanged = (scene) => {
        if (!this.broadcastActivated)
            return;
        this.buildAndPushDisplayConfig(scene);
    };
    onLiveSceneChanged = (scene) => {
        if (!this.broadcastActivated)
            return;
        this.buildAndPushLiveConfig(scene);
    };
    static THUMBNAIL_CAPTURE_DELAY_MS = 500;
    static THUMBNAIL_WIDTH = 320;
    static THUMBNAIL_HEIGHT = 180;
    static MAX_THUMBNAIL_TRANSFER_RETRIES = 3;
    buildAndPushDisplayConfig(scene) {
        if (!scene)
            return;
        const version = ++this.displayConfigVersion;
        void SceneService.getInstance()
            .resolveScene(scene)
            .then((config) => {
            if (this.displayConfigVersion !== version)
                return;
            this.streamrAppApi
                ?.getMediaManager()
                .setDisplayScene(config)
                .catch(console.error);
            this.scheduleThumbnailCapture(scene.id);
        });
    }
    scheduleThumbnailCapture(sceneId) {
        if (this.thumbnailCaptureTimer) {
            clearTimeout(this.thumbnailCaptureTimer);
        }
        this.thumbnailCaptureTimer = setTimeout(() => {
            this.thumbnailCaptureTimer = undefined;
            void this.captureThumbnail(sceneId);
        }, StreamrAppService.THUMBNAIL_CAPTURE_DELAY_MS);
    }
    async captureThumbnail(sceneId) {
        const manager = this.streamrAppApi?.getMediaManager();
        if (!manager)
            return;
        try {
            const dataUrl = await manager.captureDisplayFrame(StreamrAppService.THUMBNAIL_WIDTH, StreamrAppService.THUMBNAIL_HEIGHT);
            if (dataUrl) {
                SceneService.getNotificationInterface().notifySetSceneThumbnail(sceneId, dataUrl);
            }
        }
        catch (e) {
            console.warn('Failed to capture scene thumbnail', e);
        }
    }
    buildAndPushLiveConfig(scene) {
        const effectiveScene = scene ?? SceneService.getInstance().getDisplayScene();
        if (!effectiveScene)
            return;
        const version = ++this.liveConfigVersion;
        void SceneService.getInstance()
            .resolveScene(effectiveScene)
            .then((config) => {
            if (this.liveConfigVersion !== version)
                return;
            this.streamrAppApi
                ?.getMediaManager()
                .setLiveScene(config)
                .catch(console.error);
        });
    }
    static async setThumbnailRenderingConfig(entries) {
        const manager = StreamrAppService.getInstance().streamrAppApi?.getMediaManager();
        if (!manager)
            return false;
        await manager.setThumbnailRenderingConfig(entries);
        return true;
    }
    notifyThumbnailCanvasMounted(canvasId, canvas) {
        if (this.transferredThumbnailCanvasIds.has(canvasId))
            return;
        try {
            const offscreen = canvas.transferControlToOffscreen();
            this.transferredThumbnailCanvasIds.add(canvasId);
            this.thumbnailTransferRetries[canvasId] = 0;
            this.thumbnailCanvasMap.set(canvasId, offscreen);
            this.syncThumbnailConfig();
        }
        catch (err) {
            const retries = this.thumbnailTransferRetries[canvasId] ?? 0;
            if (retries < StreamrAppService.MAX_THUMBNAIL_TRANSFER_RETRIES) {
                this.thumbnailTransferRetries[canvasId] = retries + 1;
                this.output.rekeyThumbnailCanvas(canvasId);
            }
            else {
                console.warn(`[THUMB] transferControlToOffscreen failed after ${StreamrAppService.MAX_THUMBNAIL_TRANSFER_RETRIES} retries for "${canvasId}"`, err);
            }
        }
    }
    notifyThumbnailCanvasUnmounted(canvasId) {
        this.transferredThumbnailCanvasIds.delete(canvasId);
        this.thumbnailCanvasMap.delete(canvasId);
        this.syncThumbnailConfig();
    }
    notifySetActiveThumbnailParticipantIds(ids) {
        const hadParticipants = this.activeThumbnailParticipantIds.length > 0;
        this.activeThumbnailParticipantIds = ids;
        if (ids.length === 0 && hadParticipants) {
            this.clearThumbnailRendering();
        }
        else {
            this.syncThumbnailConfig();
        }
    }
    syncThumbnailConfig() {
        if (this.thumbnailSyncInFlight)
            return;
        const entries = [];
        const transferableCanvasIds = [];
        for (const id of this.activeThumbnailParticipantIds) {
            if (!this.thumbnailCanvasMap.has(id))
                continue;
            const canvas = this.thumbnailCanvasMap.get(id);
            const entry = {
                canvasId: id,
                trackId: id,
            };
            if (canvas) {
                entry.canvas = canvas;
                transferableCanvasIds.push(id);
            }
            entries.push(entry);
        }
        if (entries.length === 0)
            return;
        const hasCanvases = transferableCanvasIds.length > 0;
        if (hasCanvases) {
            this.thumbnailSyncInFlight = true;
        }
        const manager = this.streamrAppApi?.getMediaManager();
        if (!manager) {
            this.thumbnailSyncInFlight = false;
            return;
        }
        manager.setThumbnailRenderingConfig(entries).then(() => {
            if (hasCanvases) {
                this.thumbnailSyncInFlight = false;
                for (const id of transferableCanvasIds) {
                    this.thumbnailCanvasMap.set(id, null);
                }
            }
        }, (err) => {
            console.error('[THUMB] setThumbnailRenderingConfig error', err);
            if (hasCanvases) {
                this.thumbnailSyncInFlight = false;
            }
        });
    }
    clearThumbnailRendering() {
        const manager = this.streamrAppApi?.getMediaManager();
        if (manager) {
            manager.setThumbnailRenderingConfig([]).catch(console.error);
        }
        this.thumbnailCanvasMap.clear();
    }
    // Notifications
    notifyInitializeBroadcastFromUrl() {
        return this.initializeBroadcastFromUrl();
    }
    notifyPrepareRendering(activeBroadcast) {
        this.prepareRendering(activeBroadcast);
    }
    notifyActivateBroadcast(activeBroadcast) {
        this.streamrAppApi
            ?.getMediaManager()
            .unmuteAudio()
            .catch(console.error);
        this.activateBroadcast(activeBroadcast);
    }
    notifyDeactivateBroadcast() {
        this.deactivateBroadcast();
    }
    notifyDestroyRendering() {
        this.destroyRendering();
    }
    async notifyAddRtmpDestination(rtmpUrl, proxyWsUrl, onStatusChange) {
        if (!this.streamrAppApi) {
            console.warn('[StreamrAppService] notifyAddRtmpDestination: no active broadcast (streamrAppApi not initialized)');
            return undefined;
        }
        return this.streamrAppApi
            .getMediaManager()
            .addRtmpDestination(rtmpUrl, proxyWsUrl, onStatusChange);
    }
    async notifyRemoveRtmpDestination(destinationId) {
        if (!this.streamrAppApi) {
            console.warn('[StreamrAppService] notifyRemoveRtmpDestination: no active broadcast (streamrAppApi not initialized)');
            return;
        }
        await this.streamrAppApi
            .getMediaManager()
            .removeRtmpDestination(destinationId);
    }
    async notifyRemoveAllRtmpDestinations() {
        if (!this.streamrAppApi) {
            console.warn('[StreamrAppService] notifyRemoveAllRtmpDestinations: no active broadcast (streamrAppApi not initialized)');
            return;
        }
        await this.streamrAppApi.getMediaManager().removeAllRtmpDestinations();
    }
    notifySetAudioTrackMuted(trackId, muted) {
        this.streamrAppApi
            ?.getMediaManager()
            .setAudioTrackMuted(trackId, muted);
    }
    notifySetAudioTrackGain(trackId, gain) {
        this.streamrAppApi?.getMediaManager().setAudioTrackGain(trackId, gain);
    }
    handleTrackDisappearanceFallback(disappearedTracks, currentTracks, _currentTrackIds) {
        const sceneService = SceneService.getInstance();
        const displayScene = sceneService.getDisplayScene();
        if (!displayScene)
            return;
        const allAssignedSourceIds = new Set(displayScene.videoSlots
            .map((s) => s.sourceId)
            .filter((id) => !!id));
        for (const gone of disappearedTracks) {
            const slotsUsingIt = displayScene.videoSlots.filter((s) => s.sourceId === gone.trackId);
            if (slotsUsingIt.length === 0)
                continue;
            const replacementTrack = currentTracks.find((t) => t.userId === gone.userId &&
                !allAssignedSourceIds.has(t.trackId));
            if (!replacementTrack)
                continue;
            for (const slot of slotsUsingIt) {
                sceneService.notifyAssignSlotSource(displayScene.id, slot.id, replacementTrack.trackId);
                allAssignedSourceIds.add(replacementTrack.trackId);
            }
        }
    }
    ensurePlaceholderTracksForExistingUsers() {
        if (!this.userManager || !this.streamrAppApi)
            return;
        const mm = this.streamrAppApi.getMediaManager();
        for (const [userId, user] of this.userManager.getUsers()) {
            if (user.status === UserRole.SPEAKER ||
                user.status === UserRole.COHOST) {
                mm.createPlaceholderTrack(userId, user.displayName ?? userId);
            }
        }
    }
    syncPlaceholderVisibility(mm, tracks) {
        const ownRole = this.streamrAppApi?.getUserRole();
        if (!this.userManager ||
            !this.streamrAppApi ||
            !this.roleComposites(ownRole)) {
            return;
        }
        const usersWithRealVideo = new Set();
        for (const t of tracks) {
            if (!t.isLocal && !t.trackId.startsWith('placeholder:')) {
                usersWithRealVideo.add(canonicalUserId(t.userId));
            }
        }
        this.ensurePlaceholderTracksForActiveRemoteUsers(mm, tracks);
        const allRemoteUserIds = new Set();
        for (const [userId, user] of this.userManager.getUsers()) {
            if (user.online) {
                allRemoteUserIds.add(canonicalUserId(userId));
            }
        }
        for (const t of tracks) {
            if (!t.isLocal && !t.trackId.startsWith('placeholder:')) {
                allRemoteUserIds.add(canonicalUserId(t.userId));
            }
        }
        for (const t of this.latestAudioTracks) {
            if (!t.isLocal) {
                allRemoteUserIds.add(canonicalUserId(t.userId));
            }
        }
        mm.beginPlaceholderSync();
        for (const userId of allRemoteUserIds) {
            if (usersWithRealVideo.has(userId) ||
                mm.hasRealVideoTracksForUser(userId)) {
                mm.hidePlaceholderTrack(userId);
            }
            else {
                mm.showPlaceholderTrack(userId);
            }
        }
        mm.endPlaceholderSync();
    }
    ensurePlaceholderTracksForActiveRemoteUsers(mm, videoTracks) {
        if (!this.userManager)
            return;
        const seen = new Set();
        for (const t of videoTracks) {
            if (!t.trackId.startsWith('placeholder:') && !t.isLocal) {
                seen.add(canonicalUserId(t.userId));
            }
        }
        for (const t of this.latestAudioTracks) {
            if (!t.isLocal) {
                seen.add(canonicalUserId(t.userId));
            }
        }
        for (const userId of seen) {
            mm.createPlaceholderTrack(userId, this.userManager.resolveDisplayName(userId));
        }
    }
    rebuildFilmStripGroups() {
        const currentStatus = this.userManager?.getCurrentUserRole();
        const ownTracksOnly = currentStatus === UserRole.SPEAKER ||
            currentStatus === UserRole.GUEST;
        const groupMap = new Map();
        const orderKeys = [];
        for (const vt of this.latestVideoTracks) {
            if (ownTracksOnly && !vt.isLocal)
                continue;
            if (!groupMap.has(vt.userId)) {
                groupMap.set(vt.userId, []);
                orderKeys.push(vt.userId);
            }
            const track = {
                type: 'video',
                trackId: vt.trackId,
                userId: vt.userId,
                name: this.userManager?.resolveDisplayName(vt.userId) ??
                    vt.userId,
                isYou: vt.isLocal,
                isLocal: vt.isLocal,
            };
            const tracks = groupMap.get(vt.userId);
            if (tracks)
                tracks.push(track);
        }
        for (const at of this.latestAudioTracks) {
            if (ownTracksOnly && !at.isLocal)
                continue;
            if (!groupMap.has(at.userId)) {
                groupMap.set(at.userId, []);
                orderKeys.push(at.userId);
            }
            const track = {
                type: 'audio',
                trackId: at.trackId,
                userId: at.userId,
                muted: at.muted,
                gain: at.gain,
                isLocal: at.isLocal,
            };
            const tracks = groupMap.get(at.userId);
            if (tracks)
                tracks.push(track);
        }
        const groups = orderKeys.map((userId) => ({
            userId,
            displayName: this.userManager?.resolveDisplayName(userId) ??
                userId,
            tracks: groupMap.get(userId) ?? [],
        }));
        this.output.setFilmStripGroups(groups);
    }
    evaluateLowEndConferencing() {
        if (!this.lowEndModeEnabled ||
            !this.userManager ||
            !this.streamrAppApi) {
            if (this.output.getLowEndConferencingActive()) {
                this.deactivateLowEndConferencing();
            }
            return;
        }
        const isCall = this.output.getSessionMode() === 'call' ||
            UserServiceOutput.getAcceptedParticipantType() ===
                AcceptedParticipantType.SPEAKERS;
        if (!isCall) {
            if (this.output.getLowEndConferencingActive()) {
                this.deactivateLowEndConferencing();
            }
            return;
        }
        const remoteParticipantCount = this.countRemoteCallParticipants();
        if (remoteParticipantCount < 2) {
            if (this.output.getLowEndConferencingActive()) {
                this.deactivateLowEndConferencing();
            }
            return;
        }
        if (!this.output.getLowEndConferencingActive()) {
            this.output.setLowEndConferencingActive(true);
        }
        this.updateLowEndFocusUser();
        this.updateLowEndParticipants();
    }
    countRemoteCallParticipants() {
        if (!this.userManager)
            return 0;
        let count = 0;
        for (const [, user] of this.userManager.getUsers()) {
            if (user.online &&
                (user.status === UserRole.SPEAKER ||
                    user.status === UserRole.COHOST)) {
                count++;
            }
        }
        return count;
    }
    updateLowEndFocusUser() {
        const mm = this.streamrAppApi?.getMediaManager();
        if (!mm)
            return;
        const dominantUserId = this.output.getDominantSpeakerUserId();
        if (dominantUserId) {
            mm.setLowEndFocusUserId(dominantUserId);
            return;
        }
        const fallbackUserId = this.getFirstRemoteSpeakerUserId();
        mm.setLowEndFocusUserId(fallbackUserId);
    }
    getFirstRemoteSpeakerUserId() {
        if (!this.userManager)
            return null;
        for (const [userId, user] of this.userManager.getUsers()) {
            if (user.online &&
                (user.status === UserRole.SPEAKER ||
                    user.status === UserRole.COHOST)) {
                return canonicalUserId(userId);
            }
        }
        return null;
    }
    updateLowEndParticipants() {
        if (!this.userManager)
            return;
        const participants = [];
        const speechStates = this.output.getRemoteSpeechStates();
        for (const [userId, user] of this.userManager.getUsers()) {
            if (!user.online ||
                (user.status !== UserRole.SPEAKER &&
                    user.status !== UserRole.COHOST)) {
                continue;
            }
            const uid = canonicalUserId(userId);
            participants.push({
                userId: uid,
                displayName: this.userManager.resolveDisplayName(userId) ?? userId,
                isSpeaking: speechStates[uid]?.isSpeaking ?? false,
            });
        }
        this.output.setLowEndParticipants(participants);
    }
    deactivateLowEndConferencing() {
        this.output.setLowEndConferencingActive(false);
        this.output.setLowEndParticipants([]);
        this.streamrAppApi?.getMediaManager().setLowEndFocusUserId(null);
    }
    notifyToggleAudio() {
        const muted = this.output.getAudioMuted();
        if (muted) {
            this.streamrAppApi
                ?.getMediaManager()
                .unmuteAudio()
                .catch(console.error);
        }
        else {
            this.streamrAppApi
                ?.getMediaManager()
                .muteAudio()
                .catch(console.error);
        }
    }
    notifyUserGesture() {
        this.streamrAppApi
            ?.getMediaManager()
            .unmuteAudio()
            .catch(console.error);
    }
    onFrameReceived() {
        if (!this.output.getWatchingStartedAt()) {
            this.output.setWatchingStartedAt(new Date());
            this.output.setIsVideoStreaming(true);
        }
        this.lastFrameReceivedAt = Date.now();
        if (this.checkStreamOnlineIntervalId !== undefined) {
            this.checkStreamOnlineIntervalId = window.setInterval(() => {
                if (Date.now() - this.lastFrameReceivedAt > 5000) {
                    this.output.setIsVideoStreaming(false);
                    this.checkStreamOnlineIntervalId = undefined;
                    clearInterval(this.checkStreamOnlineIntervalId);
                    this.checkStreamOnlineIntervalId = undefined;
                }
            }, 5000);
        }
    }
    initializeBroadcastFromUrl() {
        const currentUrl = new URL(window.location.href);
        const broadcast = parseBroadcastFromUrl(currentUrl);
        if (!broadcast) {
            return false;
        }
        const cameraStreamIdParam = currentUrl.searchParams.get('cameraStreamId');
        const hashParams = new URLSearchParams(currentUrl.hash.replace(/^#/, ''));
        const cameraAesKeyParam = hashParams.get('cameraAesKey');
        const isCameraInvite = cameraStreamIdParam && cameraAesKeyParam;
        if (isCameraInvite) {
            const fullCameraStreamId = formFullStreamId(cameraStreamIdParam);
            this.pendingCameraOneToOneStreamUrl = `${fullCameraStreamId}#${cameraAesKeyParam}`;
        }
        const scheduledSession = ScheduleService.getInstance().getScheduledSession(broadcast.streamId);
        const roleProperties = scheduledSession !== undefined
            ? {
                myRole: 'broadcaster',
                broadcasterPrivateKey: scheduledSession.broadcasterPrivateKey,
            }
            : {
                myRole: 'viewer',
            };
        let resolvedStatus;
        if (isCameraInvite) {
            resolvedStatus = UserRole.CAMERA;
        }
        else if (roleProperties.myRole === 'broadcaster') {
            resolvedStatus = UserRole.COHOST;
        }
        else {
            const roleStore = new UserStore();
            const persistedRole = roleStore.getUserRole(broadcast.broadcasterUserId);
            if (persistedRole !== undefined &&
                persistedRole !== UserRole.VIEWER &&
                persistedRole !== UserRole.GUEST &&
                persistedRole !== UserRole.CAMERA) {
                resolvedStatus = persistedRole;
            }
            else {
                if (persistedRole === UserRole.GUEST ||
                    persistedRole === UserRole.CAMERA) {
                    console.log('[StreamrAppService] initializeBroadcastFromUrl: persisted role deferred to UserManager restoration', { persistedRole });
                }
                resolvedStatus = UserRole.VIEWER;
            }
        }
        UserService.getInstance().notifySetUserRole(resolvedStatus);
        const userStore = new UserStore();
        const storedBroadcasting = userStore.getBroadcastingEnabled(broadcast.streamId);
        const storedParticipantType = userStore.getAcceptedParticipantType(broadcast.streamId);
        if (storedBroadcasting !== undefined) {
            this.output.setBroadcastingEnabled(storedBroadcasting);
        }
        if (storedParticipantType !== undefined) {
            UserService.getInstance().notifyAcceptedParticipantTypeChanged(storedParticipantType);
            this.streamrAppApi
                ?.setAcceptedParticipantType(storedParticipantType)
                .catch(console.error);
        }
        BroadcastStateService.getInstance().notifySetActiveBroadcast({
            ...broadcast,
            ...roleProperties,
        });
        return true;
    }
    createStreamrAppApi(activeBroadcast) {
        const initialUserRole = activeBroadcast.myRole === 'viewer'
            ? UserRole.VIEWER
            : UserRole.COHOST;
        const streamrAppApi = new StreamrAppApi({
            streamId: activeBroadcast.streamId,
            broadcasterUserId: toUserIdRaw(activeBroadcast.broadcasterUserId),
            aesKey: hexToBinary(activeBroadcast.aesKey),
            initialUserRole,
        });
        if (activeBroadcast.myRole !== 'viewer') {
            const storedDisplayName = new UserStore().getDisplayName();
            streamrAppApi
                .getMediaManager()
                .setLocalUserId(activeBroadcast.broadcasterUserId, storedDisplayName);
            if (storedDisplayName) {
                streamrAppApi.setDisplayName(storedDisplayName);
            }
            const ografManager = OgrafManager.getInstance();
            ografManager.setMediaManager(streamrAppApi.getMediaManager());
            void OgrafService.getInstance().initialize();
        }
        streamrAppApi.getMediaManager().on('muteChange', (muted) => {
            this.output.setAudioMuted(muted);
        });
        streamrAppApi
            .getMediaManager()
            .on('audioContextBlocked', (blocked) => {
            this.output.setAudioContextBlocked(blocked);
        });
        streamrAppApi
            .getMediaManager()
            .on('userMutedChange', (userMuted) => {
            this.output.setUserMutedAudio(userMuted);
        });
        streamrAppApi
            .getMediaManager()
            .on('speechStateChanged', (isSpeaking) => {
            this.output.setLocalIsSpeaking(isSpeaking);
        });
        streamrAppApi
            .getMediaManager()
            .on('dominantSpeakerChanged', (_userSessionId, userId) => {
            this.output.setDominantSpeakerUserId(userId);
            this.evaluateLowEndConferencing();
        });
        streamrAppApi
            .getMediaManager()
            .on('remoteSpeechStateChanged', (_userSessionId, userId, isSpeaking, audioLevel) => {
            this.output.setRemoteSpeechState(userId, {
                isSpeaking,
                audioLevel,
            });
            if (this.output.getLowEndConferencingActive()) {
                this.updateLowEndParticipants();
            }
        });
        streamrAppApi.on('receivedFrame', () => {
            this.onFrameReceived();
        });
        streamrAppApi.on('broadcastMetadataChanged', (broadcastMetadata) => {
            if (this.currentStreamId) {
                ViewerCountService.getInstance().notifyViewerCountChangedForActiveStream(broadcastMetadata.viewerCount);
                PurchaseService.getInstance().notifyProductsOfActiveBroadcastChanged(broadcastMetadata.products, false);
                UserService.getInstance().notifyAcceptedParticipantTypeChanged(broadcastMetadata.acceptedParticipantType);
                this.streamrAppApi
                    ?.setAcceptedParticipantType(broadcastMetadata.acceptedParticipantType)
                    .catch(console.error);
                this.output.setBroadcastMetadataReceived(true);
                LobbyService.getInstance().notifyBroadcastMetadataReceived(broadcastMetadata.acceptedParticipantType);
            }
        });
        streamrAppApi
            .getMediaManager()
            .on('videoPropertiesChanged', (videoProperties) => {
            VideoPropertiesService.getNotificationInterface().notifyVideoPropertiesChanged(videoProperties);
        });
        streamrAppApi
            .getMediaManager()
            .on('activePublishersChanged', (publishers) => {
            this.output.setActivePublishers(publishers);
        });
        streamrAppApi
            .getMediaManager()
            .on('activeVideoTracksChanged', (tracks) => {
            if (activeBroadcast.myRole === 'viewer' &&
                tracks.length > 0 &&
                !this.output.getWatchingStartedAt()) {
                this.output.setWatchingStartedAt(new Date());
                this.output.setIsVideoStreaming(true);
            }
            const previousTracks = this.latestVideoTracks;
            this.output.setActiveVideoTracks(tracks);
            this.latestVideoTracks = tracks;
            this.rebuildFilmStripGroups();
            const currentTrackIds = new Set(tracks.map((t) => t.trackId));
            const disappeared = previousTracks.filter((t) => !currentTrackIds.has(t.trackId));
            if (disappeared.length > 0) {
                this.handleTrackDisappearanceFallback(disappeared, tracks, currentTrackIds);
            }
            if (this.roleComposites(streamrAppApi.getUserRole())) {
                const previousTrackIds = new Set(previousTracks.map((t) => t.trackId));
                const hasNewTracks = tracks.some((t) => !previousTrackIds.has(t.trackId));
                if (hasNewTracks) {
                    const displayScene = SceneService.getInstance().getDisplayScene();
                    this.buildAndPushDisplayConfig(displayScene);
                }
            }
            this.syncPlaceholderVisibility(streamrAppApi.getMediaManager(), tracks);
        });
        streamrAppApi
            .getMediaManager()
            .on('activeAudioTracksChanged', (tracks) => {
            this.output.setActiveAudioTracks(tracks);
            this.latestAudioTracks = tracks;
            this.rebuildFilmStripGroups();
            this.syncPlaceholderVisibility(streamrAppApi.getMediaManager(), this.latestVideoTracks);
        });
        // Set up services when StreamrWrapper is ready
        streamrAppApi.on('streamrWrapperReady', (streamrWrapper) => {
            const pluginApiService = PluginApiService.getInstance();
            pluginApiService.setStreamrWrapper(streamrWrapper);
            const purchaseService = PurchaseService.getInstance();
            purchaseService.setStreamrWrapper(streamrWrapper);
            // Set up UserManager
            if (this.currentBroadcast) {
                const hostUserId = toUserId(toUserIdRaw(this.currentBroadcast.broadcasterUserId));
                const ownUserId = streamrWrapper.getMyUserId();
                const ownPrivateKey = this.currentBroadcast
                    .broadcasterPrivateKey
                    ? hexToBinary(this.currentBroadcast.broadcasterPrivateKey)
                    : hexToBinary(KeyStore.getInstance().getPrivateKey(this.currentBroadcast.streamId));
                if (ownUserId !== hostUserId &&
                    this.roleAddsLocalTracks(streamrAppApi.getUserRole())) {
                    streamrAppApi
                        .getMediaManager()
                        .setLocalUserId(ownUserId, new UserStore().getDisplayName());
                }
                const storedSpeakerSecret = ownUserId !== hostUserId
                    ? new UserStore().getSpeakerSecret(hostUserId)
                    : undefined;
                const userManager = new UserManager(hostUserId, ownUserId, ownPrivateKey, storedSpeakerSecret);
                this.userManager = userManager;
                userManager.setStreamrWrapper(streamrWrapper);
                UserService.getInstance().setUserManager(userManager);
                streamrAppApi.setUserManager(userManager);
                userManager.on('displayNamesChanged', (displayNames) => {
                    this.rebuildFilmStripGroups();
                    const mm = streamrAppApi.getMediaManager();
                    for (const [uid, name] of displayNames) {
                        mm.updatePlaceholderTrack(uid, name);
                    }
                });
                userManager.on('ownRoleChanged', (newRole, oneToOneStreamUrl) => {
                    this.streamrAppApi
                        ?.getMediaManager()
                        .setExcludeLocalTracksFromMainCanvas(newRole === UserRole.GUEST)
                        .catch(console.error);
                    this.streamrAppApi
                        ?.getMediaManager()
                        .setSpeechDetectionEnabled(this.roleUsesSpeechDetection(newRole));
                    if (this.roleAddsLocalTracks(newRole) &&
                        ownUserId !== hostUserId &&
                        !this.streamrAppApi
                            ?.getMediaManager()
                            .getLocalUserId()) {
                        this.streamrAppApi
                            ?.getMediaManager()
                            .setLocalUserId(ownUserId, new UserStore().getDisplayName());
                    }
                    // Set resolution synchronously before the async
                    // setUserRole() call. React's useEffect will fire
                    // notifyStartCaptureRequested() as soon as the
                    // role propagates to the UI, so the resolution
                    // must already be set when getUserMedia runs.
                    if (this.roleAddsLocalTracks(newRole)) {
                        const profile = this.getVideoProfileForRole(newRole);
                        this.mediaService.setVideoResolution(profile.width, profile.height);
                    }
                    this.streamrAppApi
                        ?.setUserRole(newRole)
                        .then(async () => {
                        if (newRole === UserRole.GUEST &&
                            oneToOneStreamUrl &&
                            this.streamrAppApi) {
                            await this.streamrAppApi.joinOneToOnePublisher(oneToOneStreamUrl);
                            await this.streamrAppApi.waitForOneToOneAudioReady(10000);
                            await this.streamrAppApi.stopContentAudio();
                        }
                        if (newRole === UserRole.VIEWER &&
                            this.streamrAppApi) {
                            await this.streamrAppApi.resumeContentAudio();
                            await this.streamrAppApi.waitForContentAudioReady(10000);
                        }
                        if (!this.roleAddsLocalTracks(newRole)) {
                            await this.streamrAppApi
                                ?.getMediaManager()
                                .removeAllLocalTracks();
                        }
                        if (this.roleComposites(newRole)) {
                            this.ensurePlaceholderTracksForExistingUsers();
                            void this.startSpeakerPublishing();
                        }
                        else if (this.roleAddsLocalTracks(newRole)) {
                            await this.streamrAppApi
                                ?.getMediaManager()
                                .startPublishingViewerStream({
                                useDirectTracks: true,
                            });
                        }
                        if (newRole === UserRole.VIEWER) {
                            await this.streamrAppApi?.leaveOwnOneToOneStream();
                        }
                        if (this.roleUsesDirectVideoOutput(newRole)) {
                            await this.activateDirectVideoOutput();
                        }
                        else {
                            await this.deactivateDirectVideoOutput();
                        }
                    })
                        .catch(console.error);
                });
                const restoredRole = userManager.getCurrentUserRole();
                if (restoredRole !== this.streamrAppApi?.getUserRole()) {
                    console.log('[StreamrAppService] Role mismatch detected, reconciling', {
                        restoredRole,
                        engineRole: this.streamrAppApi?.getUserRole(),
                    });
                    this.streamrAppApi
                        ?.getMediaManager()
                        .setExcludeLocalTracksFromMainCanvas(restoredRole === UserRole.GUEST)
                        .catch(console.error);
                    this.streamrAppApi
                        ?.getMediaManager()
                        .setSpeechDetectionEnabled(this.roleUsesSpeechDetection(restoredRole));
                    if (this.roleAddsLocalTracks(restoredRole) &&
                        ownUserId !== hostUserId &&
                        !this.streamrAppApi
                            ?.getMediaManager()
                            .getLocalUserId()) {
                        this.streamrAppApi
                            ?.getMediaManager()
                            .setLocalUserId(ownUserId, new UserStore().getDisplayName());
                    }
                    if (this.roleAddsLocalTracks(restoredRole)) {
                        const profile = this.getVideoProfileForRole(restoredRole);
                        this.mediaService.setVideoResolution(profile.width, profile.height);
                    }
                    else {
                        this.streamrAppApi
                            ?.getMediaManager()
                            .removeAllLocalTracks()
                            .catch(console.error);
                    }
                    this.streamrAppApi
                        ?.setUserRole(restoredRole)
                        .then(async () => {
                        await this.engineStartedPromise;
                        if ((restoredRole === UserRole.VIEWER ||
                            restoredRole === UserRole.GUEST) &&
                            this.streamrAppApi) {
                            await this.streamrAppApi.activateContentPartition();
                        }
                        if (restoredRole === UserRole.GUEST &&
                            this.streamrAppApi) {
                            const storedUrl = new UserStore().getOneToOneStreamUrl(hostUserId);
                            if (storedUrl) {
                                console.log('[StreamrAppService] GUEST restore: joining one-to-one publisher', { url: storedUrl.slice(0, 40) });
                                await this.streamrAppApi.joinOneToOnePublisher(storedUrl);
                                const audioReady = await this.streamrAppApi.waitForOneToOneAudioReady(10000);
                                if (audioReady) {
                                    console.log('[StreamrAppService] GUEST restore: one-to-one audio ready, stopping content audio');
                                    await this.streamrAppApi.stopContentAudio();
                                }
                                else {
                                    console.warn('[StreamrAppService] GUEST restore: one-to-one audio timed out, re-requesting permission');
                                    this.userManager?.requestPermission(UserRole.GUEST);
                                }
                            }
                            else {
                                console.warn('[StreamrAppService] GUEST restore: no stored one-to-one URL, re-requesting permission');
                                this.userManager?.requestPermission(UserRole.GUEST);
                            }
                            await this.streamrAppApi
                                .getMediaManager()
                                .unmuteAudio();
                            console.log('[StreamrAppService] GUEST restore: audio unmuted');
                        }
                        if (this.roleComposites(restoredRole)) {
                            this.ensurePlaceholderTracksForExistingUsers();
                            void this.startSpeakerPublishing();
                        }
                        else if (this.roleAddsLocalTracks(restoredRole)) {
                            await this.streamrAppApi
                                ?.getMediaManager()
                                .startPublishingViewerStream({
                                useDirectTracks: true,
                            });
                        }
                        if (this.roleUsesDirectVideoOutput(restoredRole)) {
                            await this.activateDirectVideoOutput();
                        }
                        else {
                            await this.deactivateDirectVideoOutput();
                        }
                    })
                        .catch(console.error);
                }
                userManager.on('oneToOneStreamCreated', (userId, oneToOneStreamUrl) => {
                    this.streamrAppApi
                        ?.joinOneToOneReceiver(userId, oneToOneStreamUrl)
                        .catch(console.error);
                });
                userManager.on('userRoleChanged', (userId, newStatus, displayName) => {
                    if (newStatus === UserRole.GUEST) {
                        this.streamrAppApi
                            ?.startN1AudioForGuest(userId)
                            .catch(console.error);
                    }
                    else {
                        this.streamrAppApi
                            ?.stopN1AudioForGuest(userId)
                            .catch(console.error);
                    }
                    if (!this.roleComposites(streamrAppApi.getUserRole()))
                        return;
                    const mm = streamrAppApi.getMediaManager();
                    if (newStatus === UserRole.SPEAKER ||
                        newStatus === UserRole.COHOST) {
                        mm.createPlaceholderTrack(userId, displayName);
                        if (!mm.hasRealVideoTracksForUser(userId)) {
                            mm.showPlaceholderTrack(userId);
                        }
                    }
                    else if (newStatus === UserRole.VIEWER) {
                        this.streamrAppApi
                            ?.leaveOneToOneStream(userId)
                            .catch(console.error);
                        mm.handleUserOffline(userId);
                        mm.destroyPlaceholderTrack(userId);
                    }
                    this.evaluateLowEndConferencing();
                });
                userManager.on('userCameOnline', (userId, displayName) => {
                    const user = userManager.getUser(userId);
                    if (this.broadcastMetadataIntervalId !== undefined) {
                        this.publishBroadcastMetadataNow();
                        streamrAppApi.publishHeartbeatNow();
                    }
                    if (!this.roleComposites(streamrAppApi.getUserRole()))
                        return;
                    if (user &&
                        (user.status === UserRole.SPEAKER ||
                            user.status === UserRole.COHOST)) {
                        const mm = streamrAppApi.getMediaManager();
                        mm.createPlaceholderTrack(userId, displayName);
                        if (!mm.hasRealVideoTracksForUser(userId)) {
                            mm.showPlaceholderTrack(userId);
                        }
                    }
                    this.evaluateLowEndConferencing();
                });
                userManager.on('userWentOffline', (userId) => {
                    const mm = streamrAppApi.getMediaManager();
                    mm.handleUserOffline(userId);
                    if (this.roleComposites(streamrAppApi.getUserRole())) {
                        mm.destroyPlaceholderTrack(userId);
                    }
                    this.evaluateLowEndConferencing();
                });
            }
        });
        // Forward plugin messages to PluginApiService
        streamrAppApi.on('pluginMessage', (_message, _publisherId) => {
            // PluginApiService handles this through its StreamrWrapper listener
            // No action needed here since it's already set up in setStreamrWrapper
        });
        // Forward purchase confirmed messages to PurchaseService
        streamrAppApi.on('purchaseConfirmedMessage', (message, _publisherId) => {
            PurchaseService.getInstance().onPurchaseConfirmedMessage(message);
        });
        return streamrAppApi;
    }
    prepareRendering(activeBroadcast) {
        if (this.renderingReadyPromise)
            return;
        console.log('StreamrAppService: prepareRendering() called for stream:', activeBroadcast.streamId);
        this.currentStreamId = activeBroadcast.streamId;
        this.currentBroadcast = activeBroadcast;
        this.streamrAppApi?.stop().catch(() => undefined);
        const savedCanvas = this.pendingCanvas;
        this.pendingCanvas = undefined;
        this.canvasReadyPromise = new Promise((resolve) => {
            this.canvasReadyResolve = resolve;
        });
        this.renderingReadyPromise = new Promise((resolve) => {
            this.renderingReadyResolve = resolve;
        });
        this.engineStartedPromise = new Promise((resolve) => {
            this.engineStartedResolve = resolve;
        });
        console.log('StreamrAppService: Creating StreamrAppApi');
        this.streamrAppApi = this.createStreamrAppApi(activeBroadcast);
        executeSoon(async () => {
            if (!this.streamrAppApi)
                return;
            const privateKey = activeBroadcast.broadcasterPrivateKey
                ? hexToBinary(activeBroadcast.broadcasterPrivateKey)
                : hexToBinary(KeyStore.getInstance().getPrivateKey(activeBroadcast.streamId));
            console.log('StreamrAppService: Starting streamrAppApi with private key', activeBroadcast.broadcasterPrivateKey
                ? 'from broadcaster'
                : 'from KeyStore');
            await this.streamrAppApi.start(privateKey);
            this.engineStartedResolve?.();
            const currentApt = UserServiceOutput.getAcceptedParticipantType();
            if (currentApt !== 0) {
                await this.streamrAppApi.setAcceptedParticipantType(currentApt);
            }
            if (this.pendingCameraOneToOneStreamUrl) {
                const cameraStreamUrl = this.pendingCameraOneToOneStreamUrl;
                this.pendingCameraOneToOneStreamUrl = undefined;
                await this.streamrAppApi.joinOneToOnePublisher(cameraStreamUrl);
            }
            console.log('StreamrAppService: StreamrAppApi started');
            this.output.setReady(true);
            this.broadcastActivated = true;
            LobbyService.getInstance().notifyEngineReady();
            const currentRole = this.streamrAppApi.getUserRole();
            if (savedCanvas &&
                !this.pendingCanvas &&
                !this.roleUsesDirectVideoOutput(currentRole)) {
                await this.streamrAppApi
                    .getMediaManager()
                    .setDisplayCanvas(savedCanvas);
                this.pendingCanvas = savedCanvas;
                this.canvasReadyResolve?.();
            }
            this.streamrAppApi
                .getMediaManager()
                .setSpeechDetectionEnabled(this.roleUsesSpeechDetection(currentRole));
            if (this.roleUsesDirectVideoOutput(currentRole)) {
                await this.activateDirectVideoOutput();
            }
            if (this.roleAddsLocalTracks(currentRole)) {
                const profile = this.getVideoProfileForRole(currentRole);
                this.mediaService.setVideoResolution(profile.width, profile.height);
                await this.onMediaStreamChanged();
            }
            if (this.roleComposites(currentRole)) {
                const sceneService = SceneService.getInstance();
                const displayScene = sceneService.getDisplayScene();
                this.buildAndPushDisplayConfig(displayScene);
            }
            console.log('StreamrAppService: Rendering pipeline ready');
            this.renderingReadyResolve?.();
        });
    }
    activateBroadcast(activeBroadcast) {
        console.log('StreamrAppService: activateBroadcast() called for stream:', activeBroadcast.streamId);
        if (activeBroadcast.myRole === 'viewer') {
            this.prepareRendering(activeBroadcast);
            return;
        }
        if (!this.renderingReadyPromise) {
            this.prepareRendering(activeBroadcast);
        }
        executeSoon(async () => {
            await this.renderingReadyPromise;
            console.log('StreamrAppService: Waiting for canvas readiness');
            await this.canvasReadyPromise;
            if (!this.streamrAppApi)
                return;
            const isCallMode = this.output.getSessionMode() === 'call';
            if (isCallMode) {
                this.streamrAppApi
                    .setContentPublishingEnabled(this.output.getBroadcastingEnabled())
                    .catch(console.error);
            }
            console.log('StreamrAppService: Canvas ready, starting viewer stream publishing', { isCallMode });
            await this.streamrAppApi
                .getMediaManager()
                .startPublishingViewerStream({
                useDirectTracks: isCallMode,
            });
            this.output.setIsVideoStreaming(true);
            const sceneService = SceneService.getInstance();
            this.buildAndPushLiveConfig(sceneService.getLiveScene());
            this.startBroadcastMetadataInterval();
            console.log('StreamrAppService: Broadcast activation complete');
        });
    }
    deactivateBroadcast() {
        this.stopBroadcastMetadataInterval();
        executeSoon(async () => {
            if (this.streamrAppApi) {
                await this.streamrAppApi
                    .getMediaManager()
                    .stopPublishingViewerStream();
            }
            this.output.setIsVideoStreaming(false);
            this.clearThumbnailRendering();
            this.transferredThumbnailCanvasIds.clear();
            this.thumbnailTransferRetries = {};
            this.activeThumbnailParticipantIds = [];
            DestinationsService.getInstance().resetAllDestinations();
        });
    }
    destroyRendering() {
        this.stopBroadcastMetadataInterval();
        this.canvasReadyResolve?.();
        this.canvasReadyResolve = undefined;
        this.canvasReadyPromise = undefined;
        this.renderingReadyResolve?.();
        this.renderingReadyResolve = undefined;
        this.renderingReadyPromise = undefined;
        this.pendingCanvas = undefined;
        executeSoon(async () => {
            await this.streamrAppApi?.getMediaManager().muteAudio();
            this.output.setAudioMuted(true);
            this.output.setReady(false);
            this.output.setIsVideoStreaming(false);
            this.output.setBroadcastMetadataReceived(false);
            this.broadcastActivated = false;
            this.clearThumbnailRendering();
            this.transferredThumbnailCanvasIds.clear();
            this.thumbnailTransferRetries = {};
            this.activeThumbnailParticipantIds = [];
            await this.streamrAppApi?.stop();
            this.streamrAppApi = undefined;
            this.userManager = undefined;
            this.pendingCameraOneToOneStreamUrl = undefined;
            this.engineStartedResolve = undefined;
            this.engineStartedPromise = undefined;
            DestinationsService.getInstance().resetAllDestinations();
            this.currentStreamId = undefined;
            if (this.checkStreamOnlineIntervalId !== undefined) {
                window.clearInterval(this.checkStreamOnlineIntervalId);
                this.checkStreamOnlineIntervalId = undefined;
            }
        });
    }
    onMediaStreamChanged = async () => {
        if (this.broadcastActivated && this.streamrAppApi) {
            const currentRole = this.streamrAppApi.getUserRole();
            if (!this.roleAddsLocalTracks(currentRole))
                return;
            const manager = this.streamrAppApi.getMediaManager();
            const videoTrack = this.mediaService.getSourceVideoTrack();
            if (this.pendingCanvas || this.output.getDirectVideoTrack()) {
                await manager.addVideoTrack(videoTrack);
                if (videoTrack) {
                    const profile = this.getVideoProfileForRole(currentRole);
                    manager.setPerTrackVideoBitrate(videoTrack.id, profile.bitrate);
                }
            }
            const audioTrack = this.mediaService.getSourceAudioTrack();
            if (audioTrack) {
                await manager.addAudioTrack(audioTrack);
            }
        }
    };
    onAdditionalTrackAdded = async (track) => {
        if (!this.broadcastActivated || !this.streamrAppApi) {
            return;
        }
        const currentRole = this.streamrAppApi.getUserRole();
        if (!this.roleAddsLocalTracks(currentRole))
            return;
        const manager = this.streamrAppApi.getMediaManager();
        if (track.kind === 'video') {
            await manager.addAdditionalVideoTrack(track);
            const profile = this.getVideoProfileForRole(currentRole);
            manager.setPerTrackVideoBitrate(track.id, profile.bitrate);
        }
        else if (track.kind === 'audio') {
            await manager.addAdditionalAudioTrack(track);
        }
    };
    onAdditionalTrackRemoved = async (trackId) => {
        if (!this.broadcastActivated || !this.streamrAppApi) {
            return;
        }
        if (!this.roleAddsLocalTracks(this.streamrAppApi.getUserRole()))
            return;
        const manager = this.streamrAppApi.getMediaManager();
        await manager.removeLocalVideoTrack(trackId);
        await manager.removeLocalAudioTrack(trackId);
    };
    notifyAddCamera(deviceId) {
        this.mediaService.notifyAddCamera(deviceId);
    }
    notifyAddMicrophone(deviceId) {
        this.mediaService.notifyAddMicrophone(deviceId);
    }
    notifyShareScreen() {
        this.mediaService.notifyShareScreen();
    }
    notifyRemoveLocalTrack(trackId) {
        this.mediaService.notifyRemoveLocalTrack(trackId);
    }
    notifySetDisplayName(name) {
        this.userManager?.setLocalDisplayName(name);
        this.streamrAppApi?.setDisplayName(name);
        const localUserId = this.streamrAppApi
            ?.getMediaManager()
            .getLocalUserId();
        if (localUserId) {
            this.streamrAppApi
                ?.getMediaManager()
                .updatePlaceholderTrack(localUserId, name);
        }
    }
    notifySetSessionMode(mode, streamId) {
        this.output.setSessionMode(mode);
        const store = new UserStore();
        const storedBroadcasting = store.getBroadcastingEnabled(streamId);
        const storedParticipantType = store.getAcceptedParticipantType(streamId);
        if (mode === 'call') {
            const broadcasting = storedBroadcasting ?? false;
            const participantType = storedParticipantType ?? 1;
            this.output.setBroadcastingEnabled(broadcasting);
            store.setBroadcastingEnabled(streamId, broadcasting);
            UserService.getInstance().notifyAcceptedParticipantTypeChanged(participantType);
            store.setAcceptedParticipantType(streamId, participantType);
            this.streamrAppApi
                ?.setAcceptedParticipantType(participantType)
                .catch(console.error);
        }
        else {
            const broadcasting = storedBroadcasting ?? true;
            const participantType = storedParticipantType ?? 2;
            this.output.setBroadcastingEnabled(broadcasting);
            store.setBroadcastingEnabled(streamId, broadcasting);
            UserService.getInstance().notifyAcceptedParticipantTypeChanged(participantType);
            store.setAcceptedParticipantType(streamId, participantType);
            this.streamrAppApi
                ?.setAcceptedParticipantType(participantType)
                .catch(console.error);
        }
    }
    async notifyCreateCameraInviteUrl() {
        const broadcast = this.currentBroadcast;
        if (!broadcast || !this.streamrAppApi) {
            return undefined;
        }
        const cameraStreamId = generateStreamId();
        const cameraAesKey = crypto.getRandomValues(new Uint8Array(32));
        const oneToOneStreamUrl = `${cameraStreamId}#${binaryToHex(cameraAesKey, false)}`;
        const cameraUserId = `camera-${formShortStreamId(cameraStreamId)}`;
        await this.streamrAppApi.joinOneToOneReceiver(cameraUserId, oneToOneStreamUrl);
        const broadcastUrl = formBroadcastUrl(broadcast, '/alpha');
        const url = new URL(broadcastUrl);
        url.searchParams.set('cameraStreamId', formShortStreamId(cameraStreamId));
        const existingHash = url.hash.replace(/^#/, '');
        url.hash = `${existingHash}&cameraAesKey=${binaryToHex(cameraAesKey, false)}`;
        return url.toString();
    }
    notifyViewerLobbyDismissed() {
        this.streamrAppApi?.activateContentPartition().catch(console.error);
    }
    notifySetBroadcastingEnabled(enabled) {
        this.output.setBroadcastingEnabled(enabled);
        if (this.currentStreamId) {
            new UserStore().setBroadcastingEnabled(this.currentStreamId, enabled);
        }
        if (this.output.getSessionMode() === 'call') {
            this.streamrAppApi
                ?.setContentPublishingEnabled(enabled)
                .catch(console.error);
        }
        this.publishBroadcastMetadataNow();
    }
    notifySetAcceptedParticipantType(type) {
        UserService.getInstance().notifyAcceptedParticipantTypeChanged(type);
        if (this.currentStreamId) {
            new UserStore().setAcceptedParticipantType(this.currentStreamId, type);
        }
        this.streamrAppApi
            ?.setAcceptedParticipantType(type)
            .catch(console.error);
        this.publishBroadcastMetadataNow();
    }
    buildBroadcastMetadata() {
        return {
            viewerCount: 0,
            products: PurchaseService.getInstance().getProductsOfActiveBroadcast(),
            acceptedParticipantType: UserServiceOutput.getAcceptedParticipantType(),
        };
    }
    publishBroadcastMetadataNow() {
        this.streamrAppApi?.publishBroadcastMetadata(this.buildBroadcastMetadata());
    }
    startBroadcastMetadataInterval() {
        this.stopBroadcastMetadataInterval();
        this.publishBroadcastMetadataNow();
        this.broadcastMetadataIntervalId = setInterval(() => {
            this.publishBroadcastMetadataNow();
        }, Settings.broadcastMetadataInterval);
    }
    async startSpeakerPublishing() {
        if (!this.streamrAppApi)
            return;
        await this.canvasReadyPromise;
        if (!this.streamrAppApi)
            return;
        await this.onMediaStreamChanged();
        await this.streamrAppApi
            .getMediaManager()
            .startPublishingViewerStream({ useDirectTracks: true });
        this.output.setIsVideoStreaming(true);
        const displayScene = SceneService.getInstance().getDisplayScene();
        this.buildAndPushDisplayConfig(displayScene);
    }
    stopBroadcastMetadataInterval() {
        if (this.broadcastMetadataIntervalId !== undefined) {
            clearInterval(this.broadcastMetadataIntervalId);
            this.broadcastMetadataIntervalId = undefined;
        }
    }
}
